import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import PaymentIcon from '@mui/icons-material/Payment'
import PhoneIcon from '@mui/icons-material/Phone'
import LanguageIcon from '@mui/icons-material/Language'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import NotInterestedIcon from '@mui/icons-material/NotInterested'
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined'
import { useTranslation } from 'react-i18next'
import upperFirst from 'lodash/upperFirst'
import { isSameDay } from 'date-fns/isSameDay'

import ParticipantOptionStatusTag from '../../../events/components/ParticipantOptionStatusTag'
import { ParticipantOptions } from '../components/filterParticipantOptions'
import {
  formatHumanDateTime,
  formatHumanTime,
  useLocationDataOptions,
  Options,
} from '../../../../utils'
import { ParticipantOptionStatus, SubEventType, Locations, Rooms } from '../../../../types'

function StatusIcon({ status }: { status?: ParticipantOptionStatus | null }) {
  switch (status) {
    case ParticipantOptionStatus.PendingCheckout:
      return <PaymentIcon fontSize="small" />
    case ParticipantOptionStatus.WaitingConfirmation:
      return <TimerOutlinedIcon fontSize="small" />
    case ParticipantOptionStatus.Confirmed:
      return <CheckBoxOutlinedIcon fontSize="small" />
    case ParticipantOptionStatus.WaitingList:
      return <HourglassEmptyOutlinedIcon fontSize="small" />
    case ParticipantOptionStatus.CheckoutFailed:
    case ParticipantOptionStatus.Cancelled:
      return <NotInterestedIcon fontSize="small" />
    default:
      return <CheckBoxOutlineBlankIcon fontSize="small" />
  }
}

function LocationLabel({ location }: { location: (Locations & Options) | undefined }) {
  const { t } = useTranslation('dashboard')

  return location ? (
    <Stack
      sx={theme => ({
        backgroundColor: theme.colors.secondaryBackground,
        borderRadius: '0.5rem',
        padding: '0.2rem 0.5rem',
        border: `0.1rem solid ${theme.colors.secondary}`,
      })}
      flex="1 0 auto"
    >
      <Typography className="MuiFormControlLabel-label" fontWeight="600">
        {location.label}
      </Typography>
      <Typography fontWeight="400">
        {location.address1}
        {location.address2 ? ` (${location.address2})` : ''} {location.postcode} {location.city}
      </Typography>
      <Stack direction="row">
        <Stack direction="row" flex="1 0 auto" className="MuiFormControlLabel-label" gap="0.5rem">
          <PhoneIcon fontSize="small" />
          {location.phone_number ? (
            <a href={`tel:${location.phone_number}`}>{location.phone_number}</a>
          ) : (
            'NC'
          )}
        </Stack>
        <Stack direction="row" flex="1 0 auto" className="MuiFormControlLabel-label" gap="0.5rem">
          <LanguageIcon fontSize="small" />
          {location.website ? (
            <a href={location.website} target="_blank" rel="noreferrer">
              {t('Site web')}
            </a>
          ) : (
            'NC'
          )}
        </Stack>
      </Stack>
    </Stack>
  ) : null
}

function RoomLabel({ room }: { room: (Rooms & Options) | undefined }) {
  return room ? (
    <Stack
      sx={theme => ({
        backgroundColor: theme.colors.secondaryBackground,
        borderRadius: '0.5rem',
        padding: '0.2rem 0.5rem',
        border: `0.1rem solid ${theme.colors.secondary}`,
      })}
      flex="1 0 auto"
    >
      <Typography className="MuiFormControlLabel-label" fontWeight="600">
        {room.name}
      </Typography>
      {(room.capacity ?? room.floor) ? (
        <Typography fontWeight="400">
          {room.floor}
          {room.capacity ? ` - max ${room.capacity} personnes` : ''}
        </Typography>
      ) : null}
    </Stack>
  ) : null
}

function HotelPlaceLabel({ location }: { location: (Locations & Options) | undefined }) {
  const { t } = useTranslation('dashboard')
  return location ? (
    <LocationLabel location={location} />
  ) : (
    <Typography className="MuiFormControlLabel-label" fontWeight="600">
      {`${t("Un hôtel vous sera assigné au plus tard 72h avant l'événement")}.`}
    </Typography>
  )
}

function MealPlaceLabel({
  location,
  rooms,
}: {
  location: (Locations & Options) | undefined
  rooms: (Rooms & Options)[] | undefined
}) {
  const { t } = useTranslation('dashboard')
  const hasAnyLocation = !!location || rooms?.length
  return hasAnyLocation ? (
    <Stack direction="column" gap="0.5rem">
      <LocationLabel location={location} />
      {rooms?.map(room => (room ? <RoomLabel key={room.value} room={room} /> : null))}
    </Stack>
  ) : (
    <Typography className="MuiFormControlLabel-label" fontWeight="600">
      {`${t("Un lieu de restauration vous sera communiqué au plus tard 72h avant l'événement")}.`}
    </Typography>
  )
}

function OptionPlaceLabel({
  location,
  rooms,
}: {
  location: (Locations & Options) | undefined
  rooms: (Rooms & Options)[] | undefined
}) {
  const { t } = useTranslation('dashboard')
  const hasAnyLocation = !!location || rooms?.length
  return hasAnyLocation ? (
    <Stack direction="column" gap="0.5rem">
      <LocationLabel location={location} />
      {rooms?.map(room => <RoomLabel key={room.value} room={room} />)}
    </Stack>
  ) : (
    <Typography className="MuiFormControlLabel-label" fontWeight="600">
      {`${t("Un lieu vous sera communiqué au plus tard 72h avant l'événement")}.`}
    </Typography>
  )
}

interface RegisterDetailsProps {
  participantOptions: ParticipantOptions
}

export default function RegisterDetails({ participantOptions }: RegisterDetailsProps) {
  const locationDataOptions = useLocationDataOptions()

  const sameDay = isSameDay(participantOptions.start, participantOptions.end)
  const timeField = `${upperFirst(formatHumanDateTime(participantOptions.start))} - ${sameDay ? formatHumanTime(participantOptions.end) : formatHumanDateTime(participantOptions.end, true)}`

  const isHotel = participantOptions.subEventType === SubEventType.HotelSubEvent
  const isMeal = participantOptions.subEventType === SubEventType.MealSubEvent
  const location = isHotel
    ? locationDataOptions.find(elem => Number(elem.value) === participantOptions.hotel)
    : locationDataOptions.find(elem => Number(elem.value) === participantOptions.location)
  const rooms = participantOptions.rooms

  return (
    <Stack
      key={participantOptions.id}
      direction="row"
      gap="1rem"
      alignItems="center"
      justifyContent="space-between"
      marginLeft="-0.75rem"
    >
      <Stack direction="row" alignItems="center" gap="1rem" flex={{ sx: '0 0', lg: '1 0' }}>
        <StatusIcon status={participantOptions.status} />
        <Stack direction="column" flex="1 0 auto">
          <Typography className="MuiFormControlLabel-label">{timeField}</Typography>
          <Typography className="MuiFormControlLabel-label">
            {participantOptions.option.name}
          </Typography>

          {isHotel && <HotelPlaceLabel location={location} />}
          {isMeal && <MealPlaceLabel location={location} rooms={rooms} />}
          {!isHotel && !isMeal && rooms && <OptionPlaceLabel location={location} rooms={rooms} />}
        </Stack>
      </Stack>
      <Stack flex={{ sx: '0 0', lg: '1 0' }}>
        <ParticipantOptionStatusTag status={participantOptions.status} size="small" />
      </Stack>
    </Stack>
  )
}
